import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { useRouter } from 'next/router';
import { ParsedUrlQueryInput } from 'querystring';
import React, { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import { JobPositionLocation } from '@pflegia/data-access';
import { RegisterOrigin } from '@pflegia/types/api/candidate/register-origin';
import { UTM_REENTRY_SOURCE_COOKIE_KEY, UTM_SOURCE_COOKIE_KEY } from '@pflegia/types/common/constants/utm-keys';
import { UtmReentrySource } from '@pflegia/types/common/enums';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { useJobListSortStore } from '@pflegia/ui/hooks/use-sort';
import Button, { ButtonVariant } from '@pflegia/ui/shared/button';
import { cookieStorage } from '@pflegia/ui/storage/cookie-storage';
import { isDefined } from '@pflegia/util/common/type-guards';
import { getJobPositionPathname } from '@pflegia/util/functions/job-position/helpers';

import { FUNNEL, INTERESTED_JOB_LINK, SLIMMED_DOWN_FUNNEL, TRANSPARENT_FUNNEL } from '@shared/application/constants';
import { RegisterButtonTrackingIds } from '@shared/application/marketing/events/register-button';
import { useTracking } from '@shared/application/tracking.service';
import { useJobDetails } from '@shared/context/job-details.context';
import { useRouterHelper } from '@shared/hooks/use-router-helper';
import { MAGAZIN_UTM_CONTENT, MagazinAttribution, getMagazinUtmRecord } from '@shared/utils/magazin';

import { useNavigationHelper } from '../../hooks/use-navigation-helper';

interface JobPositionLinkAttributes {
  id: string;
  geoLocation?: JobPositionLocation | null;
  jobTitle?: string;
  slug?: string | null;
}

interface SignUpButtonProps {
  className?: string;
  colorScheme?: ButtonVariant;
  label: string;
  jobPositionAttributes?: JobPositionLinkAttributes;
  magazinAttribution?: MagazinAttribution;
}

const SignUpButton: React.FC<SignUpButtonProps> = ({
  label,
  className,
  colorScheme,
  jobPositionAttributes,
  magazinAttribution,
  ...rest
}) => {
  const authenticated = useAuth();
  const { signupButtonClickTrackEventRegistration } = useTracking();
  const { isTransparentPath, isExternalJob, isSeoLandingPage, isPreview, isJobDetailsPage, isBlog, isTransparentJobBoard } =
    useRouterHelper();
  const router = useRouter();
  const { applicationSource, registerOrigin, utmParams } = useNavigationHelper();
  const { preventApplicationForPausedPositions, isJobPositionPaused, showPausedModal } = useJobDetails();

  const handleOnApply = useCallback(() => {
    const preventApplicationHandler = () =>
      preventApplicationForPausedPositions(isJobPositionPaused, isPreview, isExternalJob, isJobDetailsPage);

    if (preventApplicationHandler && !showPausedModal) {
      const shouldPrevent = preventApplicationHandler();
      if (shouldPrevent) return;
    }

    const { sort } = useJobListSortStore.getState();
    signupButtonClickTrackEventRegistration(RegisterButtonTrackingIds.JobBoardId, sort);

    if (jobPositionAttributes) {
      if (!authenticated) {
        if (isBlog) {
          cookieStorage.setItem(UTM_REENTRY_SOURCE_COOKIE_KEY, UtmReentrySource.Blog);
        } else if (isTransparentJobBoard || isJobDetailsPage) {
          if (cookieStorage.getItem(UTM_SOURCE_COOKIE_KEY)) {
            cookieStorage.setItem(UTM_REENTRY_SOURCE_COOKIE_KEY, UtmReentrySource.Other);
          } else {
            cookieStorage.setItem(
              UTM_REENTRY_SOURCE_COOKIE_KEY,
              isTransparentJobBoard ? UtmReentrySource.OrganicLlp : UtmReentrySource.OrganicJdp,
            );
          }
        }
      }

      const { jobTitle, geoLocation, id, slug } = jobPositionAttributes;
      localStorage.setItem(INTERESTED_JOB_LINK, getJobPositionPathname({ geoLocation, id, title: jobTitle, slug }));

      const commonQueryParams = {
        jobId: jobPositionAttributes.id,
        ...(applicationSource && { applicationSource }),
      };

      const magazinUtm = magazinAttribution
        ? getMagazinUtmRecord(magazinAttribution, MAGAZIN_UTM_CONTENT.currentJobsInterested)
        : undefined;
      const effectiveRegisterOrigin = magazinAttribution ? RegisterOrigin.Blog : registerOrigin;

      if (isTransparentPath || isExternalJob || isSeoLandingPage) {
        const query: ParsedUrlQueryInput = {
          ...(!isExternalJob && { accessPoint: TRANSPARENT_FUNNEL }),
          ...(effectiveRegisterOrigin && { registerOrigin: effectiveRegisterOrigin }),
          ...commonQueryParams,
          ...(!isEmpty(utmParams) && isDefined(utmParams) && utmParams),
          ...(isDefined(magazinUtm) && magazinUtm),
        };
        router.push({ pathname: SLIMMED_DOWN_FUNNEL, query });
        return;
      }

      router.push({
        pathname: FUNNEL,
        query: {
          ...commonQueryParams,
          ...(effectiveRegisterOrigin && { registerOrigin: effectiveRegisterOrigin }),
          ...(isDefined(magazinUtm) && magazinUtm),
        },
      });
    }
  }, [
    applicationSource,
    authenticated,
    isBlog,
    isExternalJob,
    isJobDetailsPage,
    isJobPositionPaused,
    isPreview,
    isSeoLandingPage,
    isTransparentJobBoard,
    isTransparentPath,
    jobPositionAttributes,
    magazinAttribution,
    preventApplicationForPausedPositions,
    registerOrigin,
    router,
    showPausedModal,
    signupButtonClickTrackEventRegistration,
    utmParams,
  ]);

  return (
    <>
      <Button
        className={twMerge(clsx('px-8 py-3 xl:px-12', className))}
        colorScheme={colorScheme}
        label={label}
        onClick={handleOnApply}
        {...rest}
      />
    </>
  );
};

export default SignUpButton;
